/**
 * Tekst die zichzelf tijdens het scrollen letter voor letter inkleurt.
 *
 * De regels staan eerst flauw op de achtergrond; terwijl je scrollt loopt er een golf
 * overheen die elke letter kort door geel en paars trekt en daarna op de blauwe
 * tekstkleur laat landen. Scroll je terug, dan doven de letters weer uit.
 *
 * De kleuren staan niet hier maar in CSS, als custom properties op het element zelf
 * (zie common/home-header.scss): daar staat de rest van het ontwerp ook, en zo blijven
 * de merkkleuren op één plek. De eindkleur wordt niet opgegeven -- dat is gewoon de
 * `color` die het element uit de stylesheet meekrijgt.
 *
 * Gebaseerd op de Osmo Supply "Gradient Wave Text on Scroll" -- de data-attributen komen
 * daarvandaan en sturen het scrollbereik en het tempo.
 */

import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';

gsap.registerPlugin(ScrollTrigger, SplitText);

// Terugvallers voor het geval de custom properties ontbreken: flauw blauw, dan geel en
// paars uit het kleurenpalet (tailwind.config.js: primary, secondary, tertiary).
const FALLBACK_IDLE = 'rgba(0, 36, 125, 0.22)';
const FALLBACK_WAVE_ONE = '#EABC3F';
const FALLBACK_WAVE_TWO = '#A29FCA';

export default function initGradientWaveText() {
  const elements = document.querySelectorAll('[data-gradient-wave-text]');

  if (! elements.length) {
    return;
  }

  elements.forEach((element) => {
    const styles = getComputedStyle(element);

    const readColor = (property, fallback) => {
      const value = styles.getPropertyValue(property).trim();

      return value || fallback;
    };

    const scrollStart = element.getAttribute('data-gradient-wave-scroll-start') || 'top 85%';
    const scrollEnd = element.getAttribute('data-gradient-wave-scroll-end') || 'center 50%';
    const waveDuration = parseFloat(element.getAttribute('data-gradient-wave-duration')) || 0.5;
    const scrubValue = parseFloat(element.getAttribute('data-gradient-wave-scrub')) || 0.1;

    const startColor = readColor('--wave-idle', FALLBACK_IDLE);
    const waveOne = readColor('--wave-one', FALLBACK_WAVE_ONE);
    const waveTwo = readColor('--wave-two', FALLBACK_WAVE_TWO);
    const endColor = styles.color;

    // Wie aangeeft minder beweging te willen, leest de tekst gewoon in de eindkleur.
    // Dan is er ook niets te splitsen: de opmaak blijft zoals hij uit de editor komt.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    // Woorden én letters: de letters zijn wat er ingekleurd wordt, de woorden houden de
    // tekst heel bij het afbreken van regels. Zonder woordniveau zou elke letter een
    // eigen afbreekpunt worden en valt de alinea uit elkaar.
    //
    // De klassen zijn nodig voor common/gradient-wave-text.scss: de blokjes die SplitText
    // maakt erven anders de inspringing van de alinea, en dan springt élke letter in.
    new SplitText(element, {
      type: 'words, chars',
      wordsClass: 'gradient-wave-word',
      charsClass: 'gradient-wave-char',
      onSplit(self) {
        const chars = self.chars;
        const activeChars = new Set();
        const progress = { value: 0 };
        let isReady = false;

        // Bij een refresh (resize, herberekening van de trigger) staat de scrollpositie
        // al ergens midden in het bereik. De letters daar in één keer neerzetten, zonder
        // de golf af te spelen -- anders golft de hele alinea bij elke resize opnieuw.
        const syncChars = () => {
          const activeCount = Math.round(progress.value * chars.length);

          chars.forEach((char, index) => {
            const isActive = index < activeCount;

            gsap.killTweensOf(char);
            gsap.set(char, { color: isActive ? endColor : startColor });

            if (isActive) {
              activeChars.add(char);
            } else {
              activeChars.delete(char);
            }
          });
        };

        return gsap.context(() => {
          gsap.set(chars, { color: startColor });

          gsap.to(progress, {
            value: 1,
            ease: 'none',
            scrollTrigger: {
              trigger: element,
              start: scrollStart,
              end: scrollEnd,
              scrub: scrubValue,
              onRefresh: () => {
                isReady = false;
                syncChars();
                requestAnimationFrame(() => {
                  isReady = true;
                });
              },
            },
            onUpdate: () => {
              if (! isReady) {
                return;
              }

              const activeCount = Math.round(progress.value * chars.length);

              chars.forEach((char, index) => {
                const isActive = index < activeCount;

                // Net aan de beurt: geel, dan paars, dan de eigen tekstkleur. De drie
                // stappen samen duren waveDuration; het zwaartepunt ligt op de laatste,
                // zodat de letter rustig landt in plaats van in het blauw te klappen.
                if (isActive && ! activeChars.has(char)) {
                  activeChars.add(char);
                  gsap.killTweensOf(char);

                  gsap.timeline()
                    .to(char, {
                      color: waveOne,
                      duration: waveDuration * 0.25,
                      ease: 'power2.out',
                    })
                    .to(char, {
                      color: waveTwo,
                      duration: waveDuration * 0.3,
                      ease: 'power1.inOut',
                    })
                    .to(char, {
                      color: endColor,
                      duration: waveDuration * 0.45,
                      ease: 'power2.in',
                    });
                }

                // Weer voorbij tijdens het terugscrollen: uitdoven, zonder de golf.
                if (! isActive && activeChars.has(char)) {
                  activeChars.delete(char);
                  gsap.killTweensOf(char);

                  gsap.to(char, {
                    color: startColor,
                    duration: waveDuration * 0.5,
                    ease: 'none',
                  });
                }
              });
            },
          });
        }, element);
      },
    });
  });
}
